import type * as Immutable from 'immutable';
import ProductOptionTypeStore from '../../../../AppData/_stores/ProductOptionTypeStore';
import ProductStore from '../../../../AppData/_stores/ProductStore';
import PreviewStore from '../../../../AppData/_stores/PreviewStore';
import ListingStore from '../../../../UserData/_stores/Store/ListingStore';
import { StoreListingVariantPrices } from './StoreListingVariantPrices';
import { StoreVariantLimit, StoreVariantLimitResult, StoreVariantOptionTypes } from './StoreVariantLimit';

type PreviewMap = Immutable.Map<number, PreviewStore> | Immutable.OrderedMap<number, PreviewStore>

export type StoreListingValidationField =
	| 'title'
	| 'description'
	| 'storePage'
	| 'mockups'
	| 'prices'
	| 'listing'
	| `optionType:${number}`

export type StoreListingValidationErrorCode =
	| 'missing_title'
	| 'missing_description'
	| 'missing_store_page'
	| 'missing_mockups'
	| 'empty_option_type'
	| 'disabled_variant_price_option'
	| 'price_below_minimum'
	| 'store_variant_limit_exceeded'

export type StoreListingValidationError = {
	field: StoreListingValidationField
	code: StoreListingValidationErrorCode
	data?: Record<string, string | number | boolean>
	variantLimit?: StoreVariantLimitResult
}

export type StoreListingValidationResult = {
	errors: StoreListingValidationError[]
	fieldErrors: Partial<Record<StoreListingValidationField, StoreListingValidationError>>
	hasErrors: boolean
}

export type StoreListingValidationParams = {
	store: string
	listing: ListingStore
	product?: ProductStore
	availableOptionTypes: StoreVariantOptionTypes | undefined
	previews: PreviewMap | undefined
	massPresets?: boolean
	showStorePageField?: boolean
}

export const MINIMUM_LISTING_PRICE_AMOUNT = 50;
export const MINIMUM_LISTING_PRICE_LABEL = '$0.50';

export class StoreListingValidation {
	private static shouldValidateBasePrice(params: StoreListingValidationParams, variantPriceCount: number): boolean {
		if (!params.product) {
			return true;
		}

		const disabledOptionIdSet = new Set(params.listing.get('disabled_option_ids').toArray());
		let hasOptionTypeWithNoEmptyPrice = false;
		let hasOptionTypeWithOnlyOneEmptyPrice = false;

		params.availableOptionTypes?.forEach((type: ProductOptionTypeStore) => {
			if (['include-label'].includes(type.get('slug'))) {
				return;
			}

			const enabledOptions = type.get('options').filter(option => !disabledOptionIdSet.has(option.get('id')));
			if (enabledOptions.count() === 0) {
				return;
			}

			const pricedOptionSlugs = enabledOptions
				.filter(option => option.get('price_dropship').get('amount') > 0)
				.map(option => option.get('slug'))
				.toSet();
			const optionSlugCount = enabledOptions
				.map(option => option.get('slug'))
				.toSet()
				.count();

			if (pricedOptionSlugs.count() === enabledOptions.count()) {
				hasOptionTypeWithNoEmptyPrice = true;
			}

			if (enabledOptions.count() > 1 && pricedOptionSlugs.count() === optionSlugCount - 1) {
				hasOptionTypeWithOnlyOneEmptyPrice = true;
			}
		});

		const hasBasePriceRow = params.product.get('price_dropship').get('amount') > 0
			&& !hasOptionTypeWithNoEmptyPrice
			&& !hasOptionTypeWithOnlyOneEmptyPrice;

		return hasBasePriceRow || variantPriceCount === 0;
	}

	/**
	 * Builds the field key used to attach validation errors to a specific option type.
	 */
	static getOptionTypeField(optionTypeId: number): StoreListingValidationField {
		return `optionType:${optionTypeId}`;
	}

	/**
	 * Runs all frontend listing validation that can block saving or publishing.
	 *
	 * The returned errors are keyed by UI field so callers can show messages on inputs,
	 * cards, tabs, and action buttons without reimplementing the validation rules.
	 */
	static validate(params: StoreListingValidationParams): StoreListingValidationResult {
		const disabledOptionIds = params.listing.get('disabled_option_ids');
		const variantLimit = StoreVariantLimit.calculate(params.store, params.availableOptionTypes, disabledOptionIds);
		const errors: StoreListingValidationError[] = [];

		if (!params.massPresets && params.listing.get('title').trim() === "") {
			errors.push({
				field: 'title',
				code: 'missing_title',
			});
		}

		if (params.listing.get('description') === "") {
			errors.push({
				field: 'description',
				code: 'missing_description',
			});
		}

		if (params.previews !== undefined
			&& params.previews.count() > 0
			&& params.previews.every(preview => params.listing.get('disabled_preview_ids').includes(preview.get('id')))
		) {
			errors.push({
				field: 'mockups',
				code: 'missing_mockups',
			});
		}

		if (!!params.showStorePageField && (params.listing.get('squarespace_store_page_id') ?? '') === "") {
			errors.push({
				field: 'storePage',
				code: 'missing_store_page',
			});
		}

		params.availableOptionTypes?.forEach((type: ProductOptionTypeStore) => {
			if (
				!type.get('visible')
				|| type.get('options').count() <= 1
				|| StoreVariantLimit.isIgnoredOptionType(params.store, type.get('id'))
			) {
				return;
			}

			let atLeastOneOption = false;
			type.get('options').forEach((option) => {
				if (!disabledOptionIds.includes(option.get('id'))) {
					atLeastOneOption = true;
				}
			});

			if (!atLeastOneOption) {
				errors.push({
					field: StoreListingValidation.getOptionTypeField(type.get('id')),
					code: 'empty_option_type',
					data: {
						optionTypeId: type.get('id'),
						optionTypeName: type.get('name'),
					},
				});
			}
		});

		if (variantLimit?.exceeded) {
			errors.push({
				field: 'listing',
				code: 'store_variant_limit_exceeded',
				data: {
					count: variantLimit.count,
					max: variantLimit.max,
					requiresCompleteOptionMatrix: variantLimit.requiresCompleteOptionMatrix,
				},
				variantLimit,
			});
		}

		const addMinimumPriceError = () => {
			if (errors.some(error => error.field === 'prices' && error.code === 'price_below_minimum')) {
				return;
			}

			errors.push({
				field: 'prices',
				code: 'price_below_minimum',
				data: {
					minimumPrice: MINIMUM_LISTING_PRICE_LABEL,
				},
			});
		};

		let variantPrices = StoreListingVariantPrices.filterEnabled(
			params.product && params.availableOptionTypes
				? StoreListingVariantPrices.normalizeOverrides(params.listing.get('variant_prices'), params.product, params.availableOptionTypes)
				: params.listing.get('variant_prices'),
			disabledOptionIds,
		);
		if (params.product && params.availableOptionTypes) {
			// Validate the same resolved prices that the editor shows and publishing uses.
			variantPrices = variantPrices.merge(StoreListingVariantPrices.resolvePrices(
				StoreListingVariantPrices.getDefaultPrices(params.product, params.availableOptionTypes, disabledOptionIds),
				variantPrices,
				params.product,
				params.listing.get('price'),
			));
		}

		if (
			StoreListingValidation.shouldValidateBasePrice(params, variantPrices.count())
			&& params.listing.get('price').get('amount') < MINIMUM_LISTING_PRICE_AMOUNT
		) {
			addMinimumPriceError();
		}

		variantPrices.forEach(variantPrice => {
			if (variantPrice.get('price').get('amount') < MINIMUM_LISTING_PRICE_AMOUNT) {
				addMinimumPriceError();
			}
		});

		const fieldErrors: StoreListingValidationResult['fieldErrors'] = {};
		errors.forEach(error => {
			if (!fieldErrors[error.field]) {
				fieldErrors[error.field] = error;
			}
		});

		return {
			errors,
			fieldErrors,
			hasErrors: errors.length > 0,
		};
	}
}
